/**
 * Onboarding API facade — swap mock ↔ real backend here.
 *
 * When backend endpoints are ready:
 *   1. Set ONBOARDING_USE_MOCK_API = false
 *   2. Implement the `*Real` functions below (or import from blynApiClient)
 *   3. UI + onboardingService stay unchanged
 */

import { getBlynBackendBase, uploadKnowledgeSource } from "./blynApiClient";
import { blynFetch } from "./blynFetch";
import { getTenantId as resolveTenantId, getUserId as resolveUserId } from "./currentUser";
import type {
  OnboardingBrandVoiceUploadResult,
  OnboardingConnectResult,
  OnboardingInsight,
  OnboardingInsightRequest,
  OnboardingIntegrationOption,
  OnboardingOptionsResponse,
} from "./onboardingApi.types";
import {
  mockConnectOnboardingIntegration,
  mockGenerateOnboardingInsight,
  mockGetOnboardingOptions,
  mockListOnboardingIntegrations,
  mockUploadOnboardingBrandVoice,
} from "./onboardingApi.mock";

/** Flip to false when GET /onboarding/options, POST /onboarding/insight, etc. are live. */
export const ONBOARDING_USE_MOCK_API = false;

function getTenantId() {
  return resolveTenantId();
}

function getUserId() {
  return resolveUserId();
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    console.error(`[onboardingApi] API Error: ${res.status} for url: ${res.url}`);
    throw new Error(`API Error: ${res.status}`);
  }
  const json = await res.json();
  return json;
}

// ─── Real implementations (wire when backend is ready) ───────────────────────

async function getOnboardingOptionsReal(_tenantId: string): Promise<OnboardingOptionsResponse> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/onboarding/options?tenant_id=${encodeURIComponent(_tenantId)}`);
  return parseJson(res);
}

async function listOnboardingIntegrationsReal(tenantId: string): Promise<OnboardingIntegrationOption[]> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations?tenant_id=${encodeURIComponent(tenantId)}`);
  const data = await parseJson<{
    integrations?: Array<{ id: string; name: string; category?: string; status?: string }>;
    items?: Array<{ id?: string; integration_id?: string; name: string; meta?: string; category?: string; status?: string }>;
  }>(res);
  
  const rawList = data.integrations || data.items || [];
  return rawList.map((item) => ({
    integration_id: item.id || item.integration_id || "",
    name: item.name,
    desc: item.category || item.meta || "",
    status: (item.status === "success" ? "success" : item.status === "connected" ? "connected" : item.status === "available" ? "available" : "pending") as OnboardingIntegrationOption["status"],
  }));
}

async function connectOnboardingIntegrationReal(
  tenantId: string,
  integrationId: string,
): Promise<OnboardingConnectResult> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integrationId}/connect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenant_id: tenantId }),
  });
  return parseJson(res);
}

async function uploadOnboardingBrandVoiceReal(
  tenantId: string,
  file: File,
): Promise<OnboardingBrandVoiceUploadResult> {
  const data = await uploadKnowledgeSource(tenantId, file, undefined, getUserId(), "onboarding-brand-voice");
  return {
    kb_id: data.id || data.kb_id || "",
    name: (data.name as string) || "",
    index_status: data.index_status as OnboardingBrandVoiceUploadResult["index_status"],
  };
}

async function resetOnboardingReal(tenantId: string): Promise<void> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/onboarding/reset?tenant_id=${encodeURIComponent(tenantId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
}


async function generateOnboardingInsightReal(
  tenantId: string,
  body: OnboardingInsightRequest,
): Promise<OnboardingInsight> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/onboarding/insight?tenant_id=${encodeURIComponent(tenantId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function getOnboardingOptions(): Promise<OnboardingOptionsResponse> {
  const tenantId = getTenantId();
  if (ONBOARDING_USE_MOCK_API) return mockGetOnboardingOptions();
  return getOnboardingOptionsReal(tenantId);
}

export async function listOnboardingIntegrations(): Promise<OnboardingIntegrationOption[]> {
  const tenantId = getTenantId();
  if (ONBOARDING_USE_MOCK_API) return mockListOnboardingIntegrations();
  return listOnboardingIntegrationsReal(tenantId);
}

export async function connectOnboardingIntegration(integrationId: string): Promise<OnboardingConnectResult> {
  const tenantId = getTenantId();
  if (ONBOARDING_USE_MOCK_API) return mockConnectOnboardingIntegration(integrationId);
  return connectOnboardingIntegrationReal(tenantId, integrationId);
}

export async function uploadOnboardingBrandVoice(file: File): Promise<OnboardingBrandVoiceUploadResult> {
  const tenantId = getTenantId();
  if (ONBOARDING_USE_MOCK_API) return mockUploadOnboardingBrandVoice(file);
  return uploadOnboardingBrandVoiceReal(tenantId, file);
}

export async function generateOnboardingInsight(body: OnboardingInsightRequest): Promise<OnboardingInsight> {
  const tenantId = getTenantId();
  if (ONBOARDING_USE_MOCK_API) return mockGenerateOnboardingInsight(body);
  return generateOnboardingInsightReal(tenantId, body);
}

export async function resetOnboarding(): Promise<void> {
  const tenantId = getTenantId();
  if (ONBOARDING_USE_MOCK_API) return;
  return resetOnboardingReal(tenantId);
}


export type {
  OnboardingBrandVoiceUploadResult,
  OnboardingConnectResult,
  OnboardingInsight,
  OnboardingInsightCta,
  OnboardingInsightRequest,
  OnboardingIntegrationOption,
  OnboardingOptionsResponse,
  OnboardingUseCaseOption,
  OnboardingVerticalOption,
} from "./onboardingApi.types";
